import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { useCatalog } from '../../context/CatalogContext'

export default function WikiIndex() {
  // Runtime GitHub Wiki sidebar support
  const [sidebar, setSidebar] = useState<string>('')
  const [sbError, setSbError] = useState<string>('')
  const { catalog } = useCatalog()

  // Raw wikis live under /wiki/<org>/<repo> and have NO branches.
  const activeWiki = catalog?.wikis?.[0]
  const WIKI_REPO_RAW_BASE = activeWiki?.rawBase
  const WIKI_WEB_HOME = activeWiki?.webHome
  const HAS_WIKI = Boolean(WIKI_REPO_RAW_BASE)

  // Try _Sidebar.md, then Home.md
  async function loadWikiSidebar(): Promise<string | null> {
    if (!WIKI_REPO_RAW_BASE) return null
    const candidates = [`${WIKI_REPO_RAW_BASE}/_Sidebar.md`, `${WIKI_REPO_RAW_BASE}/Home.md`]

    for (const url of candidates) {
      try {
        const res = await fetch(url, { cache: 'no-cache' })
        if (!res.ok) continue
        return await res.text()
      } catch (_e) {
        // continue to next candidate
      }
    }
    return null
  }

  function normalizeWikiPage(input: string) {
    const trimmed = (input || '').replace(/^\/+|\/+$/g, '')
    const noMd = trimmed.replace(/\.md$/i, '')
    return noMd.replace(/\s+/g, '-')
  }

  const markdownComponents = {
    a({ href, children, ...props }: any) {
      const text = children as any
      const h = String(href || '')
      const isAbsolute = /^(https?:)?\/\//i.test(h)
      const isHash = h.startsWith('#')
      const isMailto = h.startsWith('mailto:')

      // Helper: rewrite absolute GitHub wiki links to in-app routes under /docs/wiki
      const rewriteAbsoluteWiki = (url: string): string | null => {
        try {
          const u = new URL(url)
          const host = u.host.toLowerCase()
          const path = u.pathname
          // Try to infer org/repo from configured webHome
          let webOrgRepo: RegExp | null = null
          try {
            if (WIKI_WEB_HOME) {
              const wh = new URL(WIKI_WEB_HOME)
              const m = /^\/(.+?)\/(.+?)\/wiki\/?$/.exec(wh.pathname)
              if (m) webOrgRepo = new RegExp(`^\/${m[1].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/${m[2].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/wiki\/(.+)$`)
            }
          } catch (_e) {
          }

          // Web wiki URLs: https://github.com/<org>/<repo>/wiki/<Page>
          const webMatch = (webOrgRepo && webOrgRepo.exec(path)) || null
          if ((host === 'github.com' || host === 'www.github.com') && webMatch) {
            const page = decodeURIComponent(webMatch[1])
            const slug = normalizeWikiPage(page).replace(/\.md$/i, '')
            const hash = u.hash || ''
            return `/docs/wiki/${slug}${hash}`
          }
          // Raw wiki URLs: https://raw.githubusercontent.com/wiki/<org>/<repo>/<Page>.md
          let rawOrgRepo: RegExp | null = null
          try {
            if (WIKI_REPO_RAW_BASE) {
              const rb = new URL(WIKI_REPO_RAW_BASE)
              const m2 = /^\/wiki\/([^/]+)\/([^/]+)\/?$/.exec(rb.pathname)
              if (m2) rawOrgRepo = new RegExp(`^\/wiki\/${m2[1].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/${m2[2].replace(/[-/\\^$*+?.()|[\]{}]/g, '')}\/(.+)\\.md$`)
            }
          } catch (_e) {
          }

          const rawMatch = (rawOrgRepo && rawOrgRepo.exec(path)) || null
          if (host === 'raw.githubusercontent.com' && rawMatch) {
            const page = decodeURIComponent(rawMatch[1])
            const slug = normalizeWikiPage(page)
            const hash = u.hash || ''
            return `/docs/wiki/${slug}${hash}`
          }
        } catch (_e) {
          // ignore parse errors
        }
        return null
      }

      if (!h || isHash || isMailto) {
        return (
          <a href={h} {...props}>
            {text}
          </a>
        )
      }

      if (isAbsolute) {
        const internal = rewriteAbsoluteWiki(h)
        if (internal) {
          return <Link to={internal}>{text}</Link>
        }
        return (
          <a href={h} target="_blank" rel="noopener noreferrer" {...props}>
            {text}
          </a>
        )
      }
      const normalized = normalizeWikiPage(h).replace(/\.md$/i, '')
      const route = `/docs/wiki/${normalized}`
      return <Link to={route}>{text}</Link>
    },
  }

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!HAS_WIKI) {
        setSbError('No wiki configured. Please define wikis[0].rawBase in catalog.json.')
        return
      }
      const text = await loadWikiSidebar()
      if (!cancelled) {
        if (text) setSidebar(text)
        else setSbError('Wiki sidebar not found at raw.githubusercontent.com/wiki (tried _Sidebar.md and Home.md).')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <section className="section api-doc-page">
      <div className="docs-layout">
        <aside className="docs-sidebar">
          <div className="docs-sidebar-inner">
            <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700 }}>Integration & App Development</h3>
            {sbError && (
              <div className="card" role="alert">
                {sbError} {WIKI_WEB_HOME && (<a href={WIKI_WEB_HOME} target="_blank" rel="noreferrer">Open on GitHub</a>)}
              </div>
            )}
            {!sbError && !sidebar && <p>Loading…</p>}
            {!sbError && sidebar && (
              <div className="prose">
                <ReactMarkdown components={markdownComponents as any}>{sidebar}</ReactMarkdown>
              </div>
            )}
          </div>
        </aside>
        <div className="docs-content">
          <div className="back-row">
            <Link to="/docs" className="back-link">← Back to docs</Link>
          </div>
          <h1 className="page-title">Integration & App Development</h1>
          <p className="page-subtitle">Guides and specifications for building integrations and apps.</p>
          <div className="prose">
            <p className="page-subtitle">Select a page from the sidebar to view it here.</p>
          </div>
        </div>
      </div>
    </section>
  )
}
